import { generatePublicOrgToken } from '@easy-expense/auth-client';
import {
  useWorkspaceStore,
  useWorkspaceKeysStore,
  useWorkspaceSettings,
  useIsAdminOrManagerRole,
} from '@easy-expense/data-firestore-client';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ReconciliationSummaryStats } from './ReconciliationSummaryStats.component';
import { ReconciliationTables } from './ReconciliationTables.component';
import { downloadExcelFile } from './downloadExcelFile';
import { reconcileStatement, ReconciledStatementRow, INTERNAL_HEADERS } from './reconcile';
import { Button } from '../Button.components';
import { MultiSelectFilter } from '../Filters/MultiSelectFilter.component';
import LoadingSpinner from '../LoadingSpinner.component';
import { useMembersAutoComplete } from '../Report/ShareReportButton';
import { CloseButton } from '../Shared/CloseButton.component';
import { Modal } from '../Shared/Modal.component';

export const StatementDetail: React.FC<{ statementKey?: string }> = ({ statementKey }) => {
  const navigate = useNavigate();
  const statement = Data.statements?.useByKey(statementKey);
  const workspaceMembers = useWorkspaceStore((s) => s.workspaceMembers);
  const { workspaceSettings } = useWorkspaceSettings();
  const membersValues = useMembersAutoComplete([]);
  const isManager = useIsAdminOrManagerRole();
  const [reconciledStatement, setReconciledStatement] = React.useState<ReconciledStatementRow[]>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const { organizationID, workspaceID } = useWorkspaceKeysStore((s) => ({
    workspaceID: s.currentWorkspaceKey,
    organizationID: s.currentOrganizationKey,
  }));

  const [token, setToken] = React.useState('');

  React.useEffect(() => {
    async function getToken() {
      if (organizationID && workspaceID) {
        const tokenResponse = await generatePublicOrgToken()({
          organizationID,
          workspaceID,
        });
        setToken(encodeURIComponent(tokenResponse.data));
      }
    }
    if (!token && organizationID && workspaceID) {
      getToken();
    }
  }, [organizationID, workspaceID, token]);

  // Re-run reconciliation when statement or token is available
  React.useEffect(() => {
    async function reconcile() {
      if (!statement || !token || !workspaceMembers) {
        return;
      }

      setIsLoading(true);

      try {
        // Convert statement transactions back to the format expected by reconcileStatement
        const statementData: DoNotFixMeIAmAny[] = statement.transactions.map((transaction) => {
          const row: DoNotFixMeIAmAny = {
            [statement.dateHeader]: transaction.date,
            [statement.totalHeader]: transaction.total,
          };
          if (statement.statementDescriptorHeader && transaction.description) {
            row[statement.statementDescriptorHeader] = transaction.description;
          }
          return row;
        });

        const result = await reconcileStatement({
          statementData,
          workspaceMembers,
          totalHeader: statement.totalHeader,
          dateHeader: statement.dateHeader,
          token,
          showTax: !!(
            workspaceSettings?.taxRates && workspaceSettings.taxRates.find((t) => t.tracked)
          ),
        });

        setReconciledStatement(result.reconciledStatement);

        if (statementKey && Data.statements) {
          const reconciledCount = result.reconciledStatement.filter(
            (r) => r.Status === 'Reconciled',
          ).length;
          const missingCount = result.reconciledStatement.filter(
            (r) => r.Status === 'Missing',
          ).length;
          const noReceiptCount = result.reconciledStatement.filter(
            (r) => r.Status === 'Reconciled' && (!r['Attachments*'] || r['Attachments*'] === ''),
          ).length;

          if (
            statement.reconciledCount !== reconciledCount ||
            statement.missingCount !== missingCount ||
            statement.noReceiptCount !== noReceiptCount
          ) {
            Data.statements.update(statementKey, {
              reconciledCount,
              missingCount,
              noReceiptCount,
            });
          }
        }
      } catch (error) {
        console.error('Error reconciling statement:', error);
      } finally {
        setIsLoading(false);
      }
    }

    reconcile();
  }, [statement, token, workspaceMembers, workspaceSettings]);

  if (!statementKey) {
    return null;
  }

  if (!Data.statements) {
    return (
      <Layout.Column center grow>
        <OpenSans.Primary>{getTranslation('Statements module is not available')}</OpenSans.Primary>
      </Layout.Column>
    );
  }

  if (!statement) {
    return (
      <Layout.Column center grow>
        <OpenSans.Primary>{getTranslation('Statement not found')}</OpenSans.Primary>
      </Layout.Column>
    );
  }

  if (isLoading) {
    return (
      <Layout.Column center grow>
        <Layout.Row center>
          <LoadingSpinner />
        </Layout.Row>
      </Layout.Column>
    );
  }

  const handleDownloadStatements = async () => {
    if (!reconciledStatement.length) {
      return;
    }

    // Reconstruct headers from statement
    const headers = [
      statement.dateHeader,
      statement.totalHeader,
      ...(statement.statementDescriptorHeader ? [statement.statementDescriptorHeader] : []),
    ];

    await downloadExcelFile(
      reconciledStatement,
      `${statement.name}-reconciled.xlsx`,
      ['Status', ...headers, ...INTERNAL_HEADERS],
      'Expenses',
      headers, // Pass original statement headers for grey highlighting
    );
  };

  const handleAssignedToChange = (selected: string[]) => {
    if (!statementKey || !Data.statements) {
      return;
    }
    Data.statements.update(statementKey, { assignedTo: selected });
  };

  const handleDeleteStatement = () => {
    if (!statementKey || !Data.statements) {
      return;
    }
    Data.statements.delete(statementKey);
    setShowDeleteModal(false);
    navigate('/reconcile');
  };

  const handleRequestMissingReceipts = () => {
    if (!statement || !statement.assignedTo || statement.assignedTo.length === 0) {
      return;
    }

    // Get emails for all assigned members
    const assignedMemberEmails = statement.assignedTo
      .map((userID) => workspaceMembers?.[userID]?.email)
      .filter((email) => email)
      .join(', ');

    if (!assignedMemberEmails) {
      return;
    }

    const statementUrl = `${window.location.origin}/statement/${statementKey}`;
    const subject = encodeURIComponent(
      `${getTranslation('Missing Receipts Request')} - ${statement.name}`,
    );
    const body = encodeURIComponent(
      `${getTranslation('Hi')},\n\n${getTranslation('Please add the missing receipts for the statement')} "${statement.name}" ${getTranslation('to Easy Expense. You can add receipts using the Easy Expense app or on your computer.')}\n\n${getTranslation('View the statement here')}: ${statementUrl}\n\n${getTranslation('Thank you')}!`,
    );

    const mailtoLink = `mailto:${assignedMemberEmails}?subject=${subject}&body=${body}`;

    // Try to open email client
    window.location.href = mailtoLink;

    // Check if email client opened by detecting window blur
    // If no blur happens within 500ms, assume email client didn't open
    let emailClientOpened = false;
    const blurHandler = () => {
      emailClientOpened = true;
    };

    window.addEventListener('blur', blurHandler);

    setTimeout(() => {
      window.removeEventListener('blur', blurHandler);
      if (!emailClientOpened) {
        // Email client didn't open, show alert with instructions
        alert(
          `${getTranslation('If your email client did not open, share this link')}:\n${statementUrl}\n\n${getTranslation('with')} ${assignedMemberEmails}`,
        );
      }
    }, 500);
  };

  return (
    <Layout.Column py={24} px={32} grow style={{ height: '100vh', overflow: 'scroll' }}>
      <CloseButton onClose={() => navigate('/reconcile')} />
      <Layout.Row style={{ minWidth: 1300 }} px={48}>
        <Spacer.Flex />
        <Layout.Column style={{ width: '100%' }}>
          <Layout.Row align justify="space-between">
            <OpenSans.Primary size={32} weight="bold-700">
              {statement.name}
            </OpenSans.Primary>
            <Layout.Row align>
              <Button.Destructive
                onClick={() => setShowDeleteModal(true)}
                content={getTranslation('Remove Statement')}
              />
              <Spacer.Horizontal size={16} />
              {reconciledStatement.length > 0 && (
                <Button.Primary
                  onClick={handleDownloadStatements}
                  content={getTranslation('Download Reconciled Statement')}
                />
              )}
            </Layout.Row>
          </Layout.Row>
          <Spacer.Vertical size={12} />

          {/* Members must not see the workspace member list; assigning is admin/manager only (EZ-3653) */}
          {isManager ? (
            <>
              <Layout.Row align>
                <MultiSelectFilter
                  header={getTranslation('Assigned To')}
                  placeholder={getTranslation('Select Members')}
                  iconicIcon="people-circle-outline"
                  selected={statement.assignedTo || []}
                  autoCompleteValues={membersValues}
                  setSelected={handleAssignedToChange}
                />
                <Spacer.Horizontal size={16} />
                {statement.assignedTo && statement.assignedTo.length > 0 && (
                  <Button.Secondary
                    onClick={handleRequestMissingReceipts}
                    content={getTranslation('Request Missing Receipts')}
                  />
                )}
              </Layout.Row>

              <Spacer.Vertical size={12} />
            </>
          ) : null}

          {reconciledStatement.length > 0 ? (
            <>
              <ReconciliationSummaryStats
                reconciledStatement={reconciledStatement}
                totalHeader={statement.totalHeader}
              />

              <Spacer.Vertical size={24} />

              <ReconciliationTables
                reconciledStatement={reconciledStatement}
                dateHeader={statement.dateHeader}
                totalHeader={statement.totalHeader}
                statementDescriptorHeader={statement.statementDescriptorHeader || undefined}
              />
            </>
          ) : (
            <Layout.Column center>
              <OpenSans.Secondary>
                {getTranslation('Loading reconciliation data...')}
              </OpenSans.Secondary>
            </Layout.Column>
          )}
        </Layout.Column>
        <Spacer.Flex />
      </Layout.Row>

      <Modal showModal={showDeleteModal} setShowModal={setShowDeleteModal}>
        <Layout.Column>
          <OpenSans.Primary size="s-16" weight="bold-700" color="primary">
            {getTranslation('Are you sure you want to remove this statement?')}
          </OpenSans.Primary>
          <Spacer.Vertical size="m-18" />
          <OpenSans.Secondary size="s-16">
            {getTranslation('This action cannot be undone.')}
          </OpenSans.Secondary>
          <Spacer.Vertical size="m-18" />
          <Layout.Row>
            <Button.Secondary
              content={getTranslation('Cancel')}
              onClick={() => setShowDeleteModal(false)}
            />
            <Spacer.Horizontal size="m-18" />
            <Button.Destructive
              content={getTranslation('Remove Statement')}
              onClick={handleDeleteStatement}
            />
          </Layout.Row>
        </Layout.Column>
      </Modal>
    </Layout.Column>
  );
};
